import {
  Goal,
  ID,
  LifeArea,
  PrefixedID,
  Relationship,
  Tag,
} from '@griply/types';

import { buildRelationshipsMap } from './build-relationships-map';
import { getArchivedGoalIds } from './derived-goal-state';
import { getGoalLifeArea } from './get-goal-life-area';
import { getSubGoals } from './goals/get-sub-goals';

export type FilterSlices = {
  tagIdsSet: Set<ID>;
  lifeAreaIdsSet: Set<ID>;
  goalIdsSet: Set<ID>;
  relationshipsMap: Record<PrefixedID, PrefixedID[]>;
  archivedGoalIds: ID[];
  lifeAreaLinkedGoalIds: ID[];
  lifeAreaGoalIdsMap: Map<ID, ID[]>;
};

export const buildFilterSlices = (
  goals: Goal[],
  tags: Tag[],
  relationships: Relationship[],
  lifeAreas: LifeArea[],
): FilterSlices => {
  const tagIdsSet = new Set(tags.map((tag) => tag.id));
  const lifeAreaIdsSet = new Set(lifeAreas.map((lifeArea) => lifeArea.id));
  const goalIdsSet = new Set(goals.map((goal) => goal.id));
  const relationshipsMap = buildRelationshipsMap(relationships);

  const archivedGoalIds = [...getArchivedGoalIds(goals)];

  // Every goal whose chain resolves to a life area — feeds the "No life area"
  // filter, which must also exclude tasks that inherit a life area from a goal.
  const lifeAreaLinkedGoalIds = goals
    .filter((goal) => getGoalLifeArea(goal, goals))
    .map(({ id }) => id);

  const lifeAreaGoalIdsMap = new Map<ID, ID[]>(
    lifeAreas.map((lifeArea): [ID, ID[]] => {
      const directGoals = goals.filter(
        (goal) => goal.lifeAreaId === lifeArea.id,
      );
      const goalIds = directGoals
        .map((goal) => [
          goal.id,
          ...getSubGoals(goal.id, { goals, recursive: true }).map(
            ({ id }) => id,
          ),
        ])
        .flat();
      return [lifeArea.id, goalIds];
    }),
  );

  return {
    tagIdsSet,
    lifeAreaIdsSet,
    goalIdsSet,
    relationshipsMap,
    archivedGoalIds,
    lifeAreaLinkedGoalIds,
    lifeAreaGoalIdsMap,
  };
};
